import { z } from 'zod'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'
import { PageQuery } from './pagination.ts'

/**
 * ══ HỢP ĐỒNG API — HOA HỒNG SALE (F27) ═══════════════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/az/hoa-hong`.
 *
 * ⚠ KHÔNG có ô TIỀN nào ở đây, và đó là chủ ý: mọi số tiền hoa hồng do MÁY CHỦ tính từ đơn và
 *   chính sách. Cho gửi lên một con số tiền là mở đường cho một khoản chi không truy được về đơn
 *   nào — thứ mà cả nghiệp vụ này tồn tại để tránh.
 */

const Ym = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ phải có dạng YYYY-MM.')

/* ══ 🧮 BẢNG TÍNH THÁNG (mô hình bản cũ `CommissionPane`) ═══════════════════════════════════════════════════════════════════ */

/**
 * ⚠ `ym` TUỲ CHỌN ở cửa đọc RT (bộ canh quyền gọi trần) — vắng thì lấy tháng hiện tại.
 */
export const MonthQuery = z.object({ ym: blankAsAbsent(Ym) })
export const MonthCalcBody = z.object({ ym: Ym, cutoff: ISODate })
export const MonthUnlockBody = z.object({ ym: Ym, version: Version })
export const MonthYmBody = z.object({ ym: Ym })
export const CultureScoreBody = z.object({ ym: Ym, saleId: Uuid, score: z.number().min(0).max(100) })
export const MonthFixedSalaryBody = z.object({ ym: Ym, saleId: Uuid, saleName: z.string().min(1), salaryVnd: z.number().min(0) })
export const BulkTagBody = z.object({
  ym: Ym,
  refs: z.array(z.object({ jobRef: z.string().min(1), saleId: Uuid })).min(1).max(500),
  branchTag: z.string().min(1, 'Nhập tên nhánh riêng.'),
  reason: z.string().min(1, 'Nhập lý do.'),
})
export const IncludeOrderParams = z.object({ jobId: Uuid })

const HhTierBody = z.object({ min: z.number().nullable(), max: z.number().nullable(), rate: z.number().min(0).max(100) })
const KpiTierBody = z.object({ target: z.number().min(0), bonus: z.number().min(0), minMonths: z.number().min(0) })
export const PolicyVersionBody = z.object({
  effectiveFrom: ISODate,
  hhTierNoiDia: z.array(HhTierBody).min(1).max(30),
  hhTierQuocTe: z.array(HhTierBody).min(1).max(30),
  kpiTiers: z.array(KpiTierBody).max(30),
  note: blankAsAbsent(z.string()),
})
export const EmpTypeBody = z.object({ userId: Uuid, empType: z.enum(['staff', 'ctv']) })

/* ══ 💸 PHÁT HOA HỒNG ═════════════════════════════════════════════════════════════════════════════════════════════════════ */

export const HoldOrderBody = z.object({ ref: z.string().min(1), saleId: Uuid, ym: Ym })
export const UnholdParams = z.object({ id: Uuid })
export const UnholdBody = z.object({ version: Version })
/** ⚠ KHÔNG có ô tiền — máy chủ dựng lại danh sách đủ điều kiện trong giao dịch; màn chỉ gửi sale nào và STK đã sửa. */
export const PhatBatchBody = z.object({
  saleIds: z.array(Uuid).min(1, 'Chưa chọn sale nào.').max(200),
  bank: z.array(z.object({ saleId: Uuid, bankName: z.string(), bankAccount: z.string() })).max(200).default([]),
})

/* ══ 📊 BÁO CÁO ══════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export const ReportQuery = z.object({
  fromYm: blankAsAbsent(Ym),
  toYm: blankAsAbsent(Ym),
  onlyLocked: blankAsAbsent(z.enum(['true', 'false'])),
})
export const SnapshotFileQuery = z.object({ ym: Ym })

export type MonthQueryT = z.infer<typeof MonthQuery>
export type MonthCalcBodyT = z.infer<typeof MonthCalcBody>
export type MonthUnlockBodyT = z.infer<typeof MonthUnlockBody>
export type MonthYmBodyT = z.infer<typeof MonthYmBody>
export type CultureScoreBodyT = z.infer<typeof CultureScoreBody>
export type MonthFixedSalaryBodyT = z.infer<typeof MonthFixedSalaryBody>
export type BulkTagBodyT = z.infer<typeof BulkTagBody>
export type IncludeOrderParamsT = z.infer<typeof IncludeOrderParams>
export type PolicyVersionBodyT = z.infer<typeof PolicyVersionBody>
export type EmpTypeBodyT = z.infer<typeof EmpTypeBody>
export type HoldOrderBodyT = z.infer<typeof HoldOrderBody>
export type UnholdParamsT = z.infer<typeof UnholdParams>
export type UnholdBodyT = z.infer<typeof UnholdBody>
export type PhatBatchBodyT = z.infer<typeof PhatBatchBody>
export type ReportQueryT = z.infer<typeof ReportQuery>
export type SnapshotFileQueryT = z.infer<typeof SnapshotFileQuery>

/* ══ LƯƠNG CỐ ĐỊNH THEO THÁNG · SỬA TAY SỐ CỦA MỘT ĐƠN ═══════════════════════════════════════ */

/**
 * ⚠ `period` TUỲ CHỌN, cùng khuôn với `HoldQuery`: cửa đọc RT bị bộ canh quyền gọi KHÔNG kèm tham
 *   số nào (`rt-stubs`), nên một ô bắt buộc ở đây biến 200 thành 400 và làm hỏng phép đo quyền.
 *   Thiếu kỳ thì trả cả hai bảng — mỗi sale mỗi tháng đúng một dòng, bảng nhỏ.
 */
export const CommissionPeriodQuery = z.object({
  period: blankAsAbsent(z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ phải có dạng YYYY-MM.')),
})

export const SetFixedSalaryBody = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ phải có dạng YYYY-MM.'),
  saleId: blankAsAbsent(Uuid),
  saleName: z.string().min(1, 'Thiếu tên người bán hàng.'),
  amount: moneyField('Lương cố định'),
  note: blankAsAbsent(z.string()),
})

/**
 * ⚠ `reason` BẮT BUỘC và hai ô tiền TUỲ CHỌN: đè ô nào thì gửi ô đó. Luật "phải đè ít nhất một ô"
 *   chốt ở `@rs/commission` để câu từ chối là tiếng Việt, không khai lần hai ở đây.
 */
export const SetOrderOverrideBody = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ phải có dạng YYYY-MM.'),
  jobRef: z.string().min(1, 'Thiếu mã đơn.'),
  saleId: blankAsAbsent(Uuid),
  saleName: z.string().optional(),
  revOverride: blankAsAbsent(moneyField('Doanh thu sửa tay')),
  costOverride: blankAsAbsent(moneyField('Giá vốn sửa tay')),
  branchTag: blankAsAbsent(z.string()),
  reason: z.string().min(1, 'Phải ghi lý do khi sửa tay số của một đơn.'),
})

export const CommissionManualIdParams = z.object({ id: Uuid })
/** Xoá đi qua `POST /:id/delete` như mọi cửa xoá khác, và cũng mang số bản (A4). */
export const DeleteCommissionManualBody = z.object({ version: Version })

export type CommissionPeriodQueryT = z.infer<typeof CommissionPeriodQuery>
export type SetFixedSalaryBodyT = z.infer<typeof SetFixedSalaryBody>
export type SetOrderOverrideBodyT = z.infer<typeof SetOrderOverrideBody>
export type CommissionManualIdParamsT = z.infer<typeof CommissionManualIdParams>
export type DeleteCommissionManualBodyT = z.infer<typeof DeleteCommissionManualBody>

/* ══ TỜ TRÌNH CHÍNH SÁCH HOA HỒNG KHÁCH (`0097`, tab 🧾 Duyệt chi ngoài) ═══════════════════════════ */

/**
 * ⚠ Dòng trống (không tuyến / số tiền 0) được GỬI LÊN bình thường và bị miền lọc bỏ — bản cũ lọc im
 *   lặng đúng như vậy, còn "phải có ít nhất một dòng" chốt ở `@rs/commission` cho câu tiếng Việt.
 */
export const CustPolicyRowBody = z.object({
  route: z.string().default(''),
  service: blankAsAbsent(z.string()),
  unitSellPrice: blankAsAbsent(moneyField('Đơn giá bán')),
  policyAmount: z.union([moneyField('Số tiền chính sách'), z.literal('')]).default(''),
  payeeName: blankAsAbsent(z.string()),
  payeeCode: blankAsAbsent(z.string()),
  bankAccount: blankAsAbsent(z.string()),
  bankName: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

export const CustPolicyBody = z.object({
  customerId: Uuid,
  docDate: ISODate,
  effectiveFrom: ISODate,
  effectiveTo: ISODate,
  rows: z.array(CustPolicyRowBody).max(100),
  managerApproverId: blankAsAbsent(Uuid),
  directorApproverId: blankAsAbsent(Uuid),
  /** `order` = lập ngay tại form đơn khi gõ tay hoa hồng (không bắt chọn người duyệt). */
  source: z.enum(['pane', 'order']).default('pane'),
})

export const EditCustPolicyBody = CustPolicyBody.omit({ source: true }).extend({ version: Version })

export const CustPolicyIdParams = z.object({ id: Uuid })
export const CustPolicyStepBody = z.object({ version: Version })
export const RejectCustPolicyBody = z.object({ version: Version, reason: z.string().default('') })

export const CustPolicyPayeeBody = z.object({
  version: Version,
  rows: z.array(z.object({
    rowId: Uuid,
    payeeName: z.string().default(''),
    payeeCode: z.string().optional(),
    bankAccount: z.string().optional(),
    bankName: z.string().optional(),
  })).min(1).max(100),
})

export const CustPolicyQuery = z.object({ limit: PageQuery.shape.limit })

export const CustPolicyMatchQuery = z.object({
  customerId: Uuid,
  routeName: z.string().min(1),
  packId: blankAsAbsent(Uuid),
  onDate: blankAsAbsent(ISODate),
})

export type CustPolicyBodyT = z.infer<typeof CustPolicyBody>
export type EditCustPolicyBodyT = z.infer<typeof EditCustPolicyBody>
export type CustPolicyIdParamsT = z.infer<typeof CustPolicyIdParams>
export type CustPolicyStepBodyT = z.infer<typeof CustPolicyStepBody>
export type RejectCustPolicyBodyT = z.infer<typeof RejectCustPolicyBody>
export type CustPolicyPayeeBodyT = z.infer<typeof CustPolicyPayeeBody>
export type CustPolicyQueryT = z.infer<typeof CustPolicyQuery>
export type CustPolicyMatchQueryT = z.infer<typeof CustPolicyMatchQuery>
